import { Button, type ButtonProps } from '@affine/component/ui/button';
import { AuthService, SubscriptionService } from '@affine/core/modules/cloud';
import type { SubscriptionRecurring } from '@affine/graphql';
import { SubscriptionPlan, SubscriptionStatus } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { CheckLargeIcon } from '@docnboard/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import clsx from 'clsx';
import { useSetAtom } from 'jotai';
import type { PropsWithChildren } from 'react';
import { useCallback } from 'react';

import { authAtom } from '../../../../atoms/auth';
import type { DynamicPrice, FixedPrice } from './cloud-plans';
import * as styles from './style.css';

interface PlanCardProps {
  detail: FixedPrice | DynamicPrice;
  recurring: SubscriptionRecurring;
  plan: SubscriptionPlan;
}

export const PlanCard = (props: PlanCardProps) => {
  const { detail, recurring } = props;
  const loggedIn = useLiveData(useService(AuthService).session.loggedIn$);

  const isCurrent = loggedIn && detail.plan === props.plan;
  // TODO пока не проверяем recurring
  // recurring === proSubscription?.recurring;

  return (
    <div data-current={isCurrent} key={detail.plan} className={styles.planCard}>
      <div className={styles.planCardBorderMock} />
      <div className={styles.planTitle}>
        <div style={{ paddingBottom: 12 }}>
          <section className={styles.planTitleName}>{detail.name}</section>
          <section className={styles.planTitleDescription}>
            {detail.description}
          </section>
          <section className={styles.planTitleTitle}>
            {detail.titleRenderer(recurring, detail as any)}
          </section>
        </div>
        <ActionButton {...props} />
      </div>
      <div className={styles.planBenefits}>
        {Object.entries(detail.benefits).map(([groupName, benefitList]) => {
          return (
            <ul className={styles.planBenefitGroup} key={groupName}>
              <section className={styles.planBenefitGroupTitle}>
                {groupName}:
              </section>
              {benefitList.map(({ icon, title }, index) => {
                return (
                  <li className={styles.planBenefit} key={index}>
                    <div className={styles.planBenefitIcon}>
                      {icon ?? <CheckLargeIcon />}
                    </div>
                    <div className={styles.planBenefitText}>{title}</div>
                  </li>
                );
              })}
            </ul>
          );
        })}
      </div>
    </div>
  );
};

const ActionButton = ({ detail, recurring }: PlanCardProps) => {
  const t = useI18n();
  const loggedIn = useLiveData(useService(AuthService).session.loggedIn$);
  const subscriptionService = useService(SubscriptionService);
  const isBeliever = useLiveData(subscriptionService.subscription.isBeliever$);
  const primarySubscription =
    useLiveData(subscriptionService.subscription.subscription$)?.[0] ?? null;
  const currentPlan = primarySubscription?.plan ?? SubscriptionPlan.Free;

  // branches:
  //  if contact                                => 'Contact Sales'
  //  if not signed in:
  //    if free                                 => 'Sign up free'
  //    else                                    => 'Buy Pro'
  //  else
  //    if isBeliever                           => 'Included in Lifetime'
  //    if isCurrent
  //      if canceled                           => 'Resume'
  //      else                                  => 'Current Plan'
  //    if isCurrent                            => 'Current Plan'
  //    else if free                            => 'Downgrade'
  //    else if currentRecurring !== recurring  => 'Change to {recurring} Billing'
  //    else                                    => 'Upgrade'

  // not signed in
  if (!loggedIn) {
    return (
      <SignUpAction>
        {detail.plan === SubscriptionPlan.Free
          ? t['com.affine.payment.sign-up-free']()
          : t['com.affine.payment.buy-pro']()}
      </SignUpAction>
    );
  }

  // lifetime
  if (isBeliever) {
    return (
      <Button className={styles.planAction} disabled>
        {t['com.affine.payment.cloud.lifetime.included']()}
      </Button>
    );
  }

  const isFree = detail.plan === SubscriptionPlan.Free;

  const isCurrent =
    detail.plan === currentPlan &&
    (isFree ? true : primarySubscription?.status === SubscriptionStatus.Active);

  // TODO временно убрали автоматическое возобновление подписки
  if (isCurrent) {
    return <CurrentPlan />;
  }

  // TODO временно убрали принудительную понижение тарифного плана
  if (
    (currentPlan === SubscriptionPlan.Basic &&
      detail.plan === SubscriptionPlan.Free) ||
    (currentPlan === SubscriptionPlan.Pro &&
      (detail.plan === SubscriptionPlan.Basic ||
        detail.plan === SubscriptionPlan.Free))
  ) {
    return null;
  }

  return <Upgrade recurring={recurring as SubscriptionRecurring} />;
};

const CurrentPlan = () => {
  const t = useI18n();
  return (
    <Button className={styles.planAction}>
      {t['com.affine.payment.current-plan']()}
    </Button>
  );
};

// const Downgrade = ({ disabled }: { disabled?: boolean }) => {
//   const t = useI18n();
//   const [open, setOpen] = useState(false);

//   const tooltipContent = disabled
//     ? t['com.affine.payment.downgraded-tooltip']()
//     : null;

//   const handleClick = useCallback(() => {
//     setOpen(true);
//   }, []);

//   return (
//     <CancelAction open={open} onOpenChange={setOpen}>
//       <Tooltip content={tooltipContent} rootOptions={{ delayDuration: 0 }}>
//         <div className={styles.planAction}>
//           <Button
//             className={styles.planAction}
//             variant="primary"
//             onClick={handleClick}
//             disabled={disabled}
//           >
//             {t['com.affine.payment.downgrade']()}
//           </Button>
//         </div>
//       </Tooltip>
//     </CancelAction>
//   );
// };

export const Upgrade = ({
  className,
  children,
  ...btnProps
}: ButtonProps & { recurring: SubscriptionRecurring }) => {
  const t = useI18n();

  return (
    <a
      href="https://docnboard.ru/#tf"
      target="_blank"
      rel="noreferrer"
      className="w-full"
    >
      <Button
        className={clsx(styles.planAction, className)}
        variant="primary"
        {...btnProps}
      >
        {children ?? t['com.affine.payment.upgrade']()}
      </Button>
    </a>
  );
};

// const ChangeRecurring = ({
//   from,
//   to,
//   disabled,
//   due,
// }: {
//   from: SubscriptionRecurring;
//   to: SubscriptionRecurring;
//   disabled?: boolean;
//   due: string;
// }) => {
//   const t = useI18n();
//   const [open, setOpen] = useState(false);
//   const [isMutating, setIsMutating] = useState(false);
//   // allow replay request on network error until component unmount or success
//   const [idempotencyKey, setIdempotencyKey] = useState(nanoid());
//   const subscription = useService(SubscriptionService).subscription;

//   const onStartChange = useCallback(() => {
//     track.$.settingsPanel.plans.changeSubscriptionRecurring({
//       plan: SubscriptionPlan.Pro,
//       recurring: to,
//     });
//     setOpen(true);
//   }, [to]);

//   const change = useAsyncCallback(async () => {
//     setIsMutating(true);
//     await subscription.setSubscriptionRecurring(idempotencyKey, to);
//     setIdempotencyKey(nanoid());
//     setIsMutating(false);
//   }, [subscription, to, idempotencyKey]);

//   const changeCurringContent = (
//     <Trans values={{ from, to, due }} className={styles.downgradeContent}>
//       You are changing your <span className={styles.textEmphasis}>{from}</span>{' '}
//       subscription to <span className={styles.textEmphasis}>{to}</span>{' '}
//       subscription. This change will take effect in the next billing cycle, with
//       an effective date of{' '}
//       <span className={styles.textEmphasis}>
//         {new Date(due).toLocaleDateString()}
//       </span>
//       .
//     </Trans>
//   );

//   return (
//     <>
//       <Button
//         className={styles.planAction}
//         variant="primary"
//         onClick={onStartChange}
//         disabled={disabled || isMutating}
//         loading={isMutating}
//       >
//         {t['com.affine.payment.change-to']({ to })}
//       </Button>

//       <ConfirmLoadingModal
//         type={'change'}
//         loading={isMutating}
//         open={open}
//         onConfirm={change}
//         onOpenChange={setOpen}
//         content={changeCurringContent}
//       />
//     </>
//   );
// };

const SignUpAction = ({ children }: PropsWithChildren) => {
  const setOpen = useSetAtom(authAtom);

  const onClickSignIn = useCallback(() => {
    setOpen(state => ({ ...state, openModal: true }));
  }, [setOpen]);

  return (
    <Button
      onClick={onClickSignIn}
      className={styles.planAction}
      variant="primary"
    >
      {children}
    </Button>
  );
};

// const ResumeButton = () => {
//   const t = useI18n();
//   const [open, setOpen] = useState(false);
//   const subscription = useService(SubscriptionService).subscription;

//   const handleClick = useCallback(() => {
//     setOpen(true);
//     const pro = subscription.pro$.value;
//     if (pro) {
//       track.$.settingsPanel.plans.resumeSubscription({
//         plan: SubscriptionPlan.Pro,
//         recurring: pro.recurring,
//       });
//     }
//   }, [subscription.pro$.value]);

//   return (
//     <ResumeAction open={open} onOpenChange={setOpen}>
//       <Button className={styles.resumeAction} onClick={handleClick}>
//         <span data-show-hover="true" className={clsx(styles.resumeContent)}>
//           {t['com.affine.payment.resume-renewal']()}
//         </span>
//         <span data-show-hover="false" className={clsx(styles.resumeContent)}>
//           {t['com.affine.payment.current-plan']()}
//         </span>
//       </Button>
//     </ResumeAction>
//   );
// };
